import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'

import type { ReportColumn } from '../models/column.types'
import {
  IMPLEMENTED_REPORT_TABLE_TYPES,
  type ReportTableCustomization,
  type ReportTableType,
} from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import { useCustomizeStore } from '../stores/customizeStore'

import { useAccount, useWorkspace } from '@/features/account'

const CACHE_KIND = 'tablePresentationV1'

interface ReportTablePresentationPreferenceV1 {
  version: 1
  visibleFields: string[]
  frozenFields: string[]
  expandedFields: string[]
  tableType: ReportTableType
  breakdownField: string | null
  frequency: TrendTableFrequency
  totalRows: Array<'other' | 'ga4'>
}

const TREND_TABLE_FREQUENCIES = [
  'daily',
  'weekly',
  'monthly',
] as const satisfies readonly TrendTableFrequency[]
const DEFAULT_FREQUENCY: TrendTableFrequency = 'daily'

export interface UseReportTablePresentationStateOptions {
  id: MaybeRefOrGetter<string>
  partitionName?: MaybeRefOrGetter<string | undefined>
  columns: MaybeRefOrGetter<readonly ReportColumn[]>
  requiredFields?: MaybeRefOrGetter<readonly string[]>
  defaultTableType?: MaybeRefOrGetter<ReportTableType>
  defaultBreakdownField?: MaybeRefOrGetter<string | null | undefined>
}

export interface UseReportTablePresentationState {
  visibleFields: Ref<string[]>
  frozenFields: Ref<string[]>
  expandedFields: Ref<string[]>
  tableType: Ref<ReportTableType>
  breakdownField: Ref<string | null>
  frequency: Ref<TrendTableFrequency>
  totalRows: Ref<Array<'other' | 'ga4'>>
  hasPreference: Readonly<Ref<boolean>>
  apply: (value: ReportTableCustomization) => void
  hydrate: (value: Partial<ReportTableCustomization>) => void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function uniqueAvailable(value: unknown, available: Set<string>): string[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  return value.flatMap((field) => {
    if (typeof field !== 'string' || !available.has(field) || seen.has(field)) return []
    seen.add(field)
    return [field]
  })
}

function isTableType(value: unknown): value is ReportTableType {
  return (IMPLEMENTED_REPORT_TABLE_TYPES as readonly unknown[]).includes(value)
}

function isTrendTableFrequency(value: unknown): value is TrendTableFrequency {
  return (TREND_TABLE_FREQUENCIES as readonly unknown[]).includes(value)
}

function requiredAvailableFields(
  columns: readonly ReportColumn[],
  requiredFields: readonly string[] | undefined,
): string[] {
  const required = new Set(requiredFields ?? [])
  return columns.filter((column) => required.has(column.field)).map((column) => column.field)
}

function includeRequiredFields(
  fields: readonly string[],
  columns: readonly ReportColumn[],
  requiredFields: readonly string[] | undefined,
): string[] {
  const result = [...fields]
  const included = new Set(result)
  for (const field of requiredAvailableFields(columns, requiredFields)) {
    if (!included.has(field)) result.push(field)
  }
  return result
}

/** Durable presentation state omitted by the legacy table-view DTO (notably freeze state). */
export function useReportTablePresentationState(
  options: UseReportTablePresentationStateOptions,
): UseReportTablePresentationState {
  const account = useAccount()
  const workspace = useWorkspace()
  const cache = useCustomizeStore()
  const visibleFields = ref<string[]>([])
  const frozenFields = ref<string[]>([])
  const expandedFields = ref<string[]>([])
  const tableType = ref<ReportTableType>('table')
  const breakdownField = ref<string | null>(null)
  const frequency = ref<TrendTableFrequency>(DEFAULT_FREQUENCY)
  const totalRows = ref<Array<'other' | 'ga4'>>(['other', 'ga4'])
  const hasPreference = ref(false)
  const partition = computed(() => {
    const userId = account.userId.value
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (userId === undefined || accountId === undefined || workspaceId === undefined) return null
    return `report-table-presentation:${userId}:${accountId}:${String(workspaceId)}:${
      toValue(options.partitionName) || 'default'
    }:${toValue(options.id)}`
  })

  function defaults(): ReportTablePresentationPreferenceV1 {
    const columns = toValue(options.columns)
    const configuredTableType = toValue(options.defaultTableType)
    return {
      version: 1,
      visibleFields: includeRequiredFields(
        columns.filter((column) => !column.invisible).map((column) => column.field),
        columns,
        toValue(options.requiredFields),
      ),
      frozenFields: columns.filter((column) => column.frozen).map((column) => column.field),
      expandedFields: [],
      tableType: isTableType(configuredTableType) ? configuredTableType : 'table',
      breakdownField: toValue(options.defaultBreakdownField) ?? null,
      frequency: DEFAULT_FREQUENCY,
      totalRows: ['other', 'ga4'],
    }
  }

  function parse(value: unknown): ReportTablePresentationPreferenceV1 | null {
    if (!isRecord(value) || value.version !== 1) return null
    const available = new Set(toValue(options.columns).map((column) => column.field))
    const visible = uniqueAvailable(value.visibleFields, available)
    const fallback = defaults()
    const visibleFields = includeRequiredFields(
      visible.length > 0 ? visible : fallback.visibleFields,
      toValue(options.columns),
      toValue(options.requiredFields),
    )
    const visibleSet = new Set(visibleFields)
    const frozenFields = uniqueAvailable(value.frozenFields, available).filter((field) =>
      visibleSet.has(field),
    )
    const numericFields = new Set(
      toValue(options.columns)
        .filter((column) => column.numeric)
        .map((column) => column.field),
    )
    const expandedFields = uniqueAvailable(value.expandedFields, available).filter(
      (field) => visibleSet.has(field) && numericFields.has(field),
    )
    const tableType = isTableType(value.tableType) ? value.tableType : fallback.tableType
    const breakdownColumn =
      typeof value.breakdownField === 'string'
        ? toValue(options.columns).find((column) => column.field === value.breakdownField)
        : undefined
    const breakdownField =
      breakdownColumn && !breakdownColumn.numeric && !breakdownColumn.isImage
        ? breakdownColumn.field
        : null
    const frequency = isTrendTableFrequency(value.frequency) ? value.frequency : fallback.frequency
    const totalRows = Array.isArray(value.totalRows)
      ? value.totalRows.filter(
          (item): item is 'other' | 'ga4' => item === 'other' || item === 'ga4',
        )
      : fallback.totalRows
    return {
      version: 1,
      visibleFields,
      frozenFields,
      expandedFields,
      tableType,
      breakdownField,
      frequency,
      totalRows: [...new Set(totalRows)],
    }
  }

  function set(value: ReportTablePresentationPreferenceV1): void {
    visibleFields.value = [...value.visibleFields]
    frozenFields.value = [...value.frozenFields]
    expandedFields.value = [...value.expandedFields]
    tableType.value = value.tableType
    breakdownField.value = value.breakdownField
    frequency.value = value.frequency
    totalRows.value = [...(value.totalRows ?? ['other', 'ga4'])]
  }

  function persist(): void {
    if (!partition.value) return
    const parsed = parse({
      version: 1,
      visibleFields: visibleFields.value,
      frozenFields: frozenFields.value,
      expandedFields: expandedFields.value,
      tableType: tableType.value,
      breakdownField: breakdownField.value,
      frequency: frequency.value,
      totalRows: totalRows.value,
    })
    if (!parsed) return
    cache.setCached(partition.value, CACHE_KIND, parsed)
    hasPreference.value = true
    set(parsed)
  }

  function hydrateFromCache(): void {
    const stored = partition.value
      ? parse(cache.getCached<unknown>(partition.value, CACHE_KIND))
      : null
    hasPreference.value = stored !== null
    set(stored ?? defaults())
  }

  watch(
    [
      partition,
      () => toValue(options.columns),
      () => toValue(options.requiredFields),
      () => toValue(options.defaultTableType),
    ],
    hydrateFromCache,
    { immediate: true, deep: true },
  )

  return {
    visibleFields,
    frozenFields,
    expandedFields,
    tableType,
    breakdownField,
    frequency,
    totalRows,
    hasPreference,
    apply: (value) => {
      visibleFields.value = [...value.visibleFields]
      frozenFields.value = [...value.frozenFields]
      expandedFields.value = [...(value.expandedFields ?? [])]
      tableType.value = value.tableType
      breakdownField.value = value.breakdownField
      frequency.value = value.frequency ?? DEFAULT_FREQUENCY
      totalRows.value = [...(value.totalRows ?? ['other', 'ga4'])]
      persist()
    },
    hydrate: (value) => {
      if (hasPreference.value) return
      const current = defaults()
      const parsed = parse({
        ...current,
        ...value,
        version: 1,
      })
      if (!parsed) return
      set(parsed)
      persist()
    },
  }
}
